import * as React from "react";
import { useEffect, useState } from "react";
import {
  Button,
  Modal,
  Steps,
  Input,
  Form,
  Result,
  Space,
  Select,
  message,
  InputNumber,
  Divider,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useStepsForm } from "sunflower-antd";

import type { SelectProps } from "antd";

import { getPlansApi } from "../../../services/Product/ProductApis";
import { getCompanyAddonsApi } from "../../../services/Addon/AddonApis";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  showModal: Function;
  groupId: any;
  handleAdd: Function;
}

let addonOptions: SelectProps["options"] = [];

let planOptions: SelectProps["options"] = [];

const AddProductModal: React.FC<IModalProps> = ({
  show,
  showModal,
  groupId,
  handleAdd,
}) => {
  const [loading, setLoading] = useState(false);
  const [planCategory, setPlanCategory] = useState<any>();
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetchAddons();
    fetchPlans();
  }, [loading]);

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
  };

  const fetchAddons = async () => {
    const { data } = await getCompanyAddonsApi(groupId);

    addonOptions = data.map((addon: any) => {
      return {
        value: addon.id,
        label: addon.Name,
      };
    });
  };

  const fetchPlans = async () => {
    const { data } = await getPlansApi();

    planOptions = data.map((plan: any) => {
      return {
        value: plan.id,
        label: plan.Name,
      };
    });
  };

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
    formLoading,
  } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      setLoading(true);
      const response = await handleAdd({ ...values, Group: Number(groupId) });
      if (response.success) {
        setLoading(false);
        return true;
      }
      setErrorMsg(response.message);
      setLoading(false);
      return false;
    },
    total: 4,
  });

  // Step Styling
  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 15 },
    labelAlign: "right" as const,
  };

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 15 },
  };

  const formList = [
    <>
      <Form.Item
        name="ProductName"
        label="Product Name"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Product Name" />
      </Form.Item>

      <Form.Item
        name="MinAge"
        label="Minimum Age"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <InputNumber
          style={{ width: "100%" }}
          size="large"
          placeholder="Min Age"
        />
      </Form.Item>
      <Form.Item
        name="MaxAge"
        label="Maximum Age"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <InputNumber
          style={{ width: "100%" }}
          size="large"
          placeholder="Max Age"
        />
      </Form.Item>
      <Form.Item
        name="PlanCategory"
        label="Plan Category"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Select
          style={{ width: "100%" }}
          size="large"
          placeholder="Plan Category"
          options={planOptions}
          onChange={(value) => setPlanCategory(value)}
        />
      </Form.Item>
      <Form.Item
        name="UnderwriterPremium"
        label="Underwriter Premium"
        rules={[
          {
            required: true,
            message: "Please enter Underwriter Premium",
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const groupPremiumValue = getFieldValue("GroupPremium");
              const policyPremiumValue = getFieldValue("PolicyPremium");

              if (
                groupPremiumValue === undefined &&
                policyPremiumValue === undefined
              ) {
                return Promise.resolve();
              }

              if (
                Number(value) < Number(groupPremiumValue) &&
                Number(value) < Number(policyPremiumValue)
              ) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error(
                  "Underwriter Premium should be less than Group Premium and Policy Premium",
                ),
              );
            },
          }),
        ]}
        dependencies={["PolicyPremium", "GroupPremium"]}
      >
        <Input size="large" placeholder="Underwriter Premium" />
      </Form.Item>
      <Form.Item
        name="GroupPremium"
        label="Group Premium"
        rules={[
          {
            required: true,
            message: "Please enter Group Premium",
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const policyPremiumValue = getFieldValue("PolicyPremium");

              if (policyPremiumValue === undefined) {
                return Promise.resolve();
              }

              if (Number(value) < Number(policyPremiumValue)) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error("Group Premium should be less than Policy Premium"),
              );
            },
          }),
        ]}
      >
        <Input size="large" placeholder="Group Premium" />
      </Form.Item>
      <Form.Item
        name="PolicyPremium"
        label="Policy Premium"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Policy Premium" />
      </Form.Item>
      <Form.Item name="Commission" label="Commission">
        <Input size="large" placeholder="Commission" />
      </Form.Item>
      <Form.Item name="CommissionTwo" label="Commission Two">
        <Input size="large" placeholder="Commission Two" />
      </Form.Item>

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button block size="large" onClick={() => gotoStep(current + 1)}>
            Next
          </Button>
        </Space>
      </Form.Item>
    </>,

    <>
      <Form.Item
        name="CoverAmount"
        label="Main Cover Amount"
        rules={[
          {
            required: true,
          },
        ]}
      >
        <Input size="large" placeholder="Main Cover Amount" />
      </Form.Item>
      {planCategory === 4 && (
        <Form.Item
          name="SpouseCoverAmount"
          label="Spouse Cover Amount"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Input size="large" placeholder="Spouse Cover Amount" />
        </Form.Item>
      )}

      <Form.Item name="Addons" label="Addons">
        <Select
          size="large"
          mode="multiple"
          placeholder="Product Addons"
          style={{ width: "100%" }}
          options={addonOptions}
        />
      </Form.Item>

      {planCategory === 4 && (
        <Form.List name="FamilyCovers">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <div key={key} style={{ marginBottom: 24 }}>
                  <Divider orientation="left">
                    Dependant Category {index + 1}
                  </Divider>
                  <Form.Item
                    {...restField}
                    name={[name, "MinAge"]}
                    label="Min Age"
                    rules={[
                      { required: true, message: "Please enter Min Age" },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Min Age"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "MaxAge"]}
                    label="Max Age"
                    rules={[
                      { required: true, message: "Please enter Max Age" },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Max Age"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    {...restField}
                    name={[name, "CoverAmount"]}
                    label="Cover Amount"
                    rules={[
                      { required: true, message: "Please enter Cover Amount" },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Cover Amount"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item {...tailLayout}>
                    <Button
                      size="large"
                      onClick={() => remove(name)}
                      style={{ marginTop: 8 }}
                      block
                      danger
                    >
                      Remove
                    </Button>
                  </Form.Item>
                </div>
              ))}
              <Form.Item {...tailLayout}>
                {fields.length < 5 && (
                  <Button
                    size="large"
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Family Cover
                  </Button>
                )}
              </Form.Item>
            </>
          )}
        </Form.List>
      )}

      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            style={{ marginBottom: 10 }}
            block
            size="large"
            loading={formLoading}
            onClick={() => {
              submit().then((result) => {
                if (result) {
                  gotoStep(current + 1);
                } else if (!result) {
                  gotoStep(current + 2);
                }
              });
            }}
          >
            Submit
          </Button>
        </Space>
      </Form.Item>
    </>,
  ];

  return (
    <Modal
      title="New Product"
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={820}
      footer={null}
    >
      {/* Steps Form */}
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 2 && (
          <div style={{ flexShrink: 0, borderRight: "1px solid #f0f0f0", paddingRight: 16, whiteSpace: "nowrap" }}>
            <Steps {...stepsProps} direction="vertical">
              <Step title="Product Info" />
              <Step title="Cover Info" />
            </Steps>
          </div>
        )}

        <div style={{ flex: 1, marginTop: 24 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 820 }}>
            {formList[current]}
          </Form>

          {current === 2 && (
            <Result status="success" title="Successfully submitted.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  block
                  size="large"
                  onClick={handleModal}
                >
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 3 && (
            <Result status="error" title={errorMsg}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddProductModal;
